import React, { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { organizationsApi } from "@/lib/api";
import { Badge } from "@/components/ui/badge";
import { Building2, ExternalLink, Loader2 } from "lucide-react";

const STATUS_COLOR = {
  "Researching": "bg-blue-500/15 text-blue-300 border-blue-500/25",
  "Potential Fit": "bg-amber-500/15 text-amber-300 border-amber-500/25",
  "Outreach": "bg-violet-500/15 text-violet-300 border-violet-500/25",
  "Active": "bg-emerald-500/15 text-emerald-300 border-emerald-500/25",
  "Partner": "bg-emerald-500/25 text-emerald-200 border-emerald-500/40",
  "Not Pursuing": "bg-white/5 text-white/50 border-white/15",
};

// Panel rendered inside the Entity workspace "Organizations" tab.
// Filters the canonical directory to organizations that have at least
// one relationship on THIS entity. This is the "file of relevant
// organizations" per entity workspace.
export default function EntityOrganizationsPanel({ entityId }) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await organizationsApi.list({ entity_id: entityId });
      setRows(r.data || []);
    } catch (e) { /* silent — surface via directory */ }
    finally { setLoading(false); }
  }, [entityId]);
  useEffect(() => { load(); }, [load]);

  if (loading) return (
    <div className="flex items-center justify-center py-8 text-white/40" data-testid="entity-orgs-loading">
      <Loader2 className="w-5 h-5 animate-spin mr-2" /> Loading organizations…
    </div>
  );
  if (rows.length === 0) return (
    <div className="rounded-xl border border-dashed border-white/10 bg-white/[0.01] p-10 text-center"
         data-testid="entity-orgs-empty">
      <Building2 className="w-6 h-6 mx-auto text-white/30 mb-2" />
      <p className="text-sm text-white/50">No organizations linked to this entity yet.</p>
      <p className="text-xs text-white/30 mt-1">
        Organizations discovered via Intake or converted from Notes will appear here.
      </p>
    </div>
  );
  return (
    <div className="space-y-3" data-testid="entity-orgs-panel">
      {rows.map(o => {
        const rel = (o.relationships || []).find(r => r.entity_id === entityId);
        return (
          <Link to={`/organizations/${o.id}`} key={o.id}
                className="block p-4 rounded-xl border border-white/10 bg-white/[0.02] hover:border-white/25 transition-colors">
            <div className="flex items-start justify-between gap-3 flex-wrap">
              <div className="min-w-0">
                <div className="text-white font-medium">{o.name}</div>
                <div className="flex items-center gap-2 text-xs text-white/60 mt-1 flex-wrap">
                  <Badge variant="outline" className="border-white/15 text-white/70 bg-white/5">{o.organization_type}</Badge>
                  {o.website && (
                    <a href={o.website} target="_blank" rel="noopener noreferrer"
                       onClick={e => e.stopPropagation()}
                       className="inline-flex items-center gap-1 hover:text-white/85 truncate">
                      {o.domain || o.website} <ExternalLink className="w-3 h-3" />
                    </a>
                  )}
                </div>
              </div>
              {rel && (
                <span className={`text-[11px] px-2 py-0.5 rounded-md border ${STATUS_COLOR[rel.relationship_status]}`}>
                  {rel.relationship_status}
                </span>
              )}
            </div>
            {rel?.why_it_matters && (
              <p className="text-xs text-white/70 mt-2 line-clamp-2 leading-relaxed">{rel.why_it_matters}</p>
            )}
          </Link>
        );
      })}
    </div>
  );
}
